import Typography from "@mui/material/Typography";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useBasicSettingsData } from "api/basic-settings";
import { hasAnyAccessToResource, hasModifyAccessToResource } from "api/general";
import { ReactComponent as Award01Icon } from "components/Icons/award-01.svg";
import { ReactComponent as Award02Icon } from "components/Icons/award-02.svg";
import { ReactComponent as BadgeIcon } from "components/Icons/badge.svg";
import { ReactComponent as BarChartCircle01Icon } from "components/Icons/bar-chart-circle-01.svg";
import { ReactComponent as ClockRefreshIcon } from "components/Icons/clock-refresh.svg";
import { ReactComponent as CodeSquare02Icon } from "components/Icons/code-square-02.svg";
import { ReactComponent as CoinsSwap02Icon } from "components/Icons/coins-swap-02.svg";
import { ReactComponent as CreditCard01Icon } from "components/Icons/credit-card-01.svg";
import { ReactComponent as Database01Icon } from "components/Icons/database-01.svg";
import { ReactComponent as Dataflow01Icon } from "components/Icons/dataflow-01.svg";
import { ReactComponent as Dataflow03Icon } from "components/Icons/dataflow-03.svg";
import { ReactComponent as Dataflow04Icon } from "components/Icons/dataflow-04.svg";
import { ReactComponent as FolderIcon } from "components/Icons/folder.svg";
import { ReactComponent as Gift01Icon } from "components/Icons/gift-01.svg";
import { ReactComponent as Gift02Icon } from "components/Icons/gift-02.svg";
import { ReactComponent as Globe01Icon } from "components/Icons/globe-01.svg";
import { ReactComponent as LayoutAlt01Icon } from "components/Icons/layout-alt-01.svg";
import { ReactComponent as LayoutGrid02Icon } from "components/Icons/layout-grid-02.svg";
import { ReactComponent as ListIcon } from "components/Icons/list.svg";
import { ReactComponent as MarkerPin01Icon } from "components/Icons/marker-pin-01.svg";
import { ReactComponent as PlayCircleIcon } from "components/Icons/play-circle.svg";
import { ReactComponent as PlayIcon } from "components/Icons/play.svg";
import { ReactComponent as PodiumIcon } from "components/Icons/podium.svg";
import { ReactComponent as Rows01Icon } from "components/Icons/rows-01.svg";
import { ReactComponent as Settings02Icon } from "components/Icons/settings-02.svg";
import { ReactComponent as Signal01Icon } from "components/Icons/signal-01.svg";
import { ReactComponent as SwitchHorizontal01Icon } from "components/Icons/switch-horizontal-01.svg";
import { ReactComponent as Trophy01Icon } from "components/Icons/trophy-01.svg";
import { ReactComponent as UserCircleIcon } from "components/Icons/user-circle.svg";
import { ReactComponent as Users01Icon } from "components/Icons/users-01.svg";
import { ReactComponent as Users02Icon } from "components/Icons/users-02.svg";
import { ReactComponent as UsersPlusIcon } from "components/Icons/users-plus.svg";
import { ReactComponent as Wallet02Icon } from "components/Icons/wallet-02.svg";
import { ReactComponent as Wallet03Icon } from "components/Icons/wallet-03.svg";
import {
  FEATURE_INTRO_DATES,
  NewFeature,
} from "components/NewFeature/NewFeature";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useLocation, useParams } from "react-router-dom";
import { LinkType } from "types/sidebar";
import { checkIfFFExists } from "utilities/env";
import { links } from "utilities/links";

export const useMenu = () => {
  const location = useLocation();
  const params = useParams();
  const settingsData = useBasicSettingsData("tiers");
  const tiersMode = settingsData.formValues?.tiersMode;
  const isLeaderboardsFeatureEnabled = checkIfFFExists("LEADERBOARDS");
  const hasAnyTransactionPermission = hasAnyAccessToResource("TRANSACTION");
  const hasAnyTierPermission = hasAnyAccessToResource("LEVEL");
  const hasAnyCampaignPermission = hasAnyAccessToResource("CAMPAIGN");
  const hasModifyCampaignPermission = hasModifyAccessToResource("CAMPAIGN");
  const hasModifyCustomEventPermission =
    hasModifyAccessToResource("CUSTOM_EVENT");
  const hasAnyCustomEventPermission = hasAnyAccessToResource("CUSTOM_EVENT");
  const hasAnyImportPermission = hasAnyAccessToResource("IMPORT");
  const hasAnyExportPermission = hasAnyAccessToResource("EXPORT");
  const hasAnyBillableReportsPermission =
    hasAnyAccessToResource("BILLABLE_REPORTS");
  const hasAnyDataAnalyticsPermission =
    hasAnyAccessToResource("DATA_ANALYTICS");
  const hasModifyWalletPermission = hasModifyAccessToResource("WALLET");
  const hasModifyRewardsPermission = hasModifyAccessToResource("REWARDS");
  const hasAnyAccesstoSystemLogs = hasAnyAccessToResource("AUDIT");
  const hasAnyChannelPermission = hasAnyAccessToResource("CHANNEL");
  const hasAnyWalletPermission = hasAnyAccessToResource("WALLET");
  const hasAnyRewardsPermission = hasAnyAccessToResource("REWARDS");
  const hasAnyCustomerPermission = hasAnyAccessToResource("CUSTOMER");
  const hasAnyBadgePermission = hasAnyAccessToResource("BADGE");
  const hasConfigDuplicataionPermission =
    hasModifyCustomEventPermission ||
    hasModifyCampaignPermission ||
    hasModifyWalletPermission ||
    hasModifyRewardsPermission;

  const useStyles = makeStyles((theme: OLTheme) =>
    createStyles({
      container: { display: "flex", gap: theme.spacing(2) },
      parent: {
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "21px",
        color: theme.gray500,
      },
      item: {
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "21px",
        color: theme.gray500,
      },
    }),
  );

  function BreadcrumbLink({
    parent,
    currentPage,
  }: {
    parent: string;
    currentPage: string;
  }) {
    const classes = useStyles();
    const { message } = useMessage();

    return (
      <div className={classes.container}>
        <Typography className={classes.parent}>
          {message({ id: parent })}
        </Typography>
        /
        <Typography className={classes.item}>
          {message({ id: currentPage })}
        </Typography>
      </div>
    );
  }

  const memberSubLinks = [
    {
      label: "listOfMembers",
      link: links.membersList,
      icon: <Users01Icon />,
    },
    {
      label: "segments",
      link: links.segmentsList,
      icon: <Rows01Icon />,
    },
    {
      label: "referredMembers",
      link: links.referredMembersList,
      icon: <Users02Icon />,
    },
  ];
  const campaignSubLinks = [
    ...(hasAnyCampaignPermission
      ? [
          {
            label: "listOfCampaigns",
            link: links.campaignsList,
            icon: <ListIcon />,
          },
        ]
      : []),
    ...(hasAnyCampaignPermission
      ? [
          {
            label: "campaignSimulator",
            link: links.simulateCampaign,
            icon: <PlayIcon />,
          },
        ]
      : []),
  ];

  const transactionsSubLinks = [
    ...(hasAnyTransactionPermission
      ? [
          {
            link: links.transactionsList,
            label: "listOfTransactions",
            icon: <CoinsSwap02Icon />,
          },
        ]
      : []),
    ...(hasAnyChannelPermission
      ? [
          {
            icon: <MarkerPin01Icon />,
            link: links.channelList,
            label: "transactionChannels",
          },
        ]
      : []),
  ];

  const walletSubLinks = [
    {
      label: "typesOfWallets",
      link: links.walletTypesList,
      icon: <Wallet02Icon />,
    },
    {
      label: "unitTransfers",
      link: links.unitTransfers,
      icon: <SwitchHorizontal01Icon />,
    },
  ];
  const rewardSubLinks = [
    {
      label: "listOfRewards",
      link: links.rewardsList,
      icon: <Gift02Icon />,
    },
    {
      label: "rewardsFulfillment",
      link: links.rewardFulfillmentsList,
      icon: <Signal01Icon />,
    },
    {
      label: "rewardsCategories",
      link: links.rewardCategoriesList,
      icon: <FolderIcon />,
    },
  ];

  const globalManagementSubLinks = [
    ...(hasAnyDataAnalyticsPermission
      ? [
          {
            label: "analytics",
            link: links.globalAnalytics,
            icon: <BarChartCircle01Icon />,
          },
        ]
      : []),
    {
      label: "settings",
      link: links.settings,
      icon: <Settings02Icon />,
    },
    ...(hasConfigDuplicataionPermission
      ? [
          {
            label: "configDuplication",
            link: links.configDuplication,
            icon: <Dataflow01Icon />,
          },
        ]
      : []),
    ...(hasAnyBillableReportsPermission
      ? [
          {
            label: "usage",
            link: links.usage,
            icon: <CreditCard01Icon />,
          },
        ]
      : []),
  ];
  const newMemberSubLinks = [
    {
      label: "generalInfo",
      link: links.singleMemberGeneralInfo(params.id),
      icon: <UserCircleIcon />,
    },
    ...(hasAnyWalletPermission
      ? [
          {
            label: "wallets",
            link: links.singleMemberWallets(params.id),
            icon: <Wallet02Icon />,
          },
        ]
      : []),

    {
      label: "timeline",
      link: links.singleMemberTimeline(params.id),
      icon: <ClockRefreshIcon />,
    },
    ...(hasAnyTierPermission
      ? [
          {
            label: "tiers",
            link: links.singleMemberTiers(params.id),
            icon: <Trophy01Icon />,
          },
        ]
      : []),
    ...(hasAnyTransactionPermission
      ? [
          {
            label: "transactions",
            link: links.singleMemberTransactions(params.id),
            icon: <CoinsSwap02Icon />,
          },
        ]
      : []),
    ...(hasAnyCustomEventPermission
      ? [
          {
            label: "customEvents",
            link: links.singleMemberCustomEvents(params.id),
            icon: <CodeSquare02Icon />,
          },
        ]
      : []),
    ...(hasAnyCampaignPermission
      ? [
          {
            label: "campaigns",
            link: links.singleMemberCampaigns(params.id),
            icon: <PlayCircleIcon />,
          },
        ]
      : []),
    ...(hasAnyCampaignPermission
      ? [
          {
            label: "achievements",
            link: links.singleMemberAchievements(params.id),
            icon: <Award01Icon />,
          },
        ]
      : []),
    ...(hasAnyRewardsPermission
      ? [
          {
            label: "rewards",
            link: links.singleMemberRewards(params.id),
            icon: <Gift01Icon />,
          },
        ]
      : []),
    {
      label: "referrals",
      link: links.singleMemberReferrals(params.id),
      icon: <UsersPlusIcon />,
    },
    ...(hasAnyBadgePermission
      ? [
          {
            label: "badges",
            link: links.singleMemberBadges(params.id),
            icon: <BadgeIcon />,
            badge: (
              <NewFeature
                featureIntroductionDate={FEATURE_INTRO_DATES.BADGES}
              />
            ),
          },
        ]
      : []),
    ...(hasAnyCampaignPermission && isLeaderboardsFeatureEnabled
      ? [
          {
            label: "leaderboards",
            link: links.singleMemberLeaderboards(params.id),
            icon: <PodiumIcon />,
            badge: (
              <NewFeature
                featureIntroductionDate={FEATURE_INTRO_DATES.LEADERBOARDS}
              />
            ),
          },
        ]
      : []),
  ];

  const generalLinks = [
    {
      icon: <LayoutAlt01Icon />,
      link: links.dashboard,
      label: "dashboard",
    },

    ...(hasAnyCustomerPermission
      ? [
          {
            icon: <Users01Icon />,
            items: memberSubLinks,
            label: "members",
          },
        ]
      : []),
    {
      icon: <Dataflow03Icon />,
      link: links.webhookList,
      label: "webhooks",
    },
  ];

  const administratorLinks = [
    {
      icon: <Globe01Icon />,
      label: "globalManagement",
      items: globalManagementSubLinks,
    },
  ];

  const eventsLinks: LinkType[] = [
    ...(hasAnyCustomEventPermission
      ? [
          {
            label: "customEvents",
            link: links.customEventsList,
            icon: <CodeSquare02Icon />,
          },
        ]
      : []),
    ...(hasAnyTransactionPermission || hasAnyChannelPermission
      ? [
          {
            label: "transactions",
            icon: <CoinsSwap02Icon />,
            items: transactionsSubLinks,
          },
        ]
      : []),
  ];

  const loayaltyModulesLinks = [
    ...(hasAnyCampaignPermission
      ? [
          {
            label: "automations",
            link: links.automationsList,
            icon: <Dataflow04Icon />,
          },
        ]
      : []),
    ...(hasAnyCampaignPermission
      ? [
          {
            icon: <ListIcon />,
            items: campaignSubLinks,
            label: "campaigns",
          },
        ]
      : []),
    ...(isLeaderboardsFeatureEnabled
      ? [
          {
            icon: <PodiumIcon />,
            link: links.leaderboardsList,
            label: "leaderboards",
            badge: (
              <NewFeature
                featureIntroductionDate={FEATURE_INTRO_DATES.LEADERBOARDS}
              />
            ),
          },
        ]
      : []),
    ...(hasAnyCampaignPermission
      ? [
          {
            icon: <Users01Icon />,
            link: links.referralsList,
            label: "referralCampaigns",
          },
        ]
      : []),
    ...(hasAnyTierPermission
      ? [
          {
            icon: <Trophy01Icon />,
            link:
              tiersMode && tiersMode === "custom"
                ? links.tierSetList
                : links.tiersList,
            label: "tiers",
          },
        ]
      : []),
    {
      icon: <Wallet03Icon />,
      items: walletSubLinks,
      label: "wallets",
    },
    ...(hasAnyCampaignPermission
      ? [
          {
            icon: <Award02Icon />,
            link: links.achievementsList,
            label: "achievements",
          },
        ]
      : []),
    {
      icon: <Gift01Icon />,
      items: rewardSubLinks,
      label: "rewards",
    },
    {
      icon: <FolderIcon />,
      link: links.collectionsList,
      label: "collections",
    },
  ];

  const otherLinks: LinkType[] = [
    ...(hasAnyImportPermission || hasAnyExportPermission
      ? [
          {
            icon: <Database01Icon />,
            link: links.importsExports,
            label: "importsExports",
          },
        ]
      : []),
    ...(hasAnyAccesstoSystemLogs
      ? [
          {
            icon: <LayoutGrid02Icon />,
            link: links.systemLogs,
            label: "systemLogs",
          },
        ]
      : []),
  ];

  const getInitialSubMenu = () => {
    switch (location.pathname) {
      case links.membersList:
      case links.segmentsList:
      case links.referredMembersList:
      case links.addSegment:
      case links.editSegment(params.id):
      case links.segment(params.id):
        return {
          header: "members",
          links: memberSubLinks,
        };
      case links.transactionsList:
      case links.addTransaction:
      case links.editTransaction(params.id):
      case links.transaction(params.id):
      case links.channelList:
      case links.addChannel:
      case links.editChannel(params.id):
        return {
          header: "transactions",
          links: transactionsSubLinks,
        };
      case links.campaignsList:
      case links.simulateCampaign:
      case links.campaign(params.id):
      case links.addCampaign:
      case links.editCampaign(params.id):
      case links.editImportedCampaign:
      case links.campaignsImportList:
        return {
          header: "campaigns",
          links: campaignSubLinks,
        };
      case links.walletTypesList:
      case links.addWalletType:
      case links.editWalletType(params.id):
      case links.wallet(params.id):
      case links.unitTransfers:
        return {
          header: "wallets",
          links: walletSubLinks,
        };
      case links.rewardsList:
      case links.rewardFulfillmentsList:
      case links.rewardCategoriesList:
      case links.addReward:
      case links.editReward(params.id):
      case links.rewardsConfiguration:
      case links.reward(params.id):
        return {
          header: "rewards",
          links: rewardSubLinks,
        };
      // Paths included in global management scope
      case links.globalAnalytics:
      case links.settings:
      case links.usage:
      case links.addRole:
      case links.editRole(params.id):
      case links.addAdmin:
      case links.editAdmin(params.id):
      case links.addStore:
      case links.editStore(params.id):
      case links.addLanguage:
      case links.editLanguage(params.id):
      case links.editTranslation(params.id):
      case links.configDuplication:
        return {
          header: "globalManagement",
          links: globalManagementSubLinks,
        };
      // Paths included in new members
      case links.singleMemberGeneralInfo(params.id):
      case links.singleMemberWallets(params.id):
      case links.singleMemberTimeline(params.id):
      case links.singleMemberTiers(params.id):
      case links.singleMemberTransactions(params.id):
      case links.singleMemberCustomEvents(params.id):
      case links.singleMemberCampaigns(params.id):
      case links.singleMemberAchievements(params.id):
      case links.singleMemberRewards(params.id):
      case links.singleMemberReferrals(params.id):
      case links.singleMemberBadges(params.id):
      case links.singleMemberLeaderboards(params.id):
        return {
          header: (
            <BreadcrumbLink parent="members" currentPage="memberProfile" />
          ),
          links: newMemberSubLinks,
        };
      case links.dashboard:
        return null;
    }

    return null;
  };

  return {
    generalLinks,
    eventsLinks,
    loayaltyModulesLinks,
    otherLinks,
    administratorLinks,
    getInitialSubMenu,
  };
};

export const isGlobalManagementView = (currentPath: string) =>
  currentPath.includes("global-management");

export const isSingleMemberView = (currentPath: string) =>
  currentPath.includes("single-member");
